import {
  Badge,
  BodyText,
  Button,
  Checkbox,
  Field,
  FieldError,
  HeaderText,
  toast,
} from "@/components/ui";
import { LogLevel, logMessage } from "@/lib/monitoring";
import { useUser } from "@/network/useUser";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import { GripVertical } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm, useFormState } from "react-hook-form";
import { z } from "zod";
import {
  MEDICAL_RECORD_SECTIONS,
  type MedicalRecordSection,
} from "../constants/medicalRecordSections";
import { useMedicalRecordsPreferences } from "../hooks/useMedicalRecordsPreferences";

const medicalRecordsPreferencesSchema = z.object({
  selectedSections: z
    .array(z.string())
    .max(5, "You can only select up to 5 sections for your Quick View."),
});

type MedicalRecordsPreferencesFormValues = z.infer<
  typeof medicalRecordsPreferencesSchema
>;

interface SortableSelectedSectionProps {
  section: MedicalRecordSection;
  index: number;
  onRemove: (sectionKey: string) => void;
}

const SortableSelectedSection = ({
  section,
  index,
  onRemove,
}: SortableSelectedSectionProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center space-x-3 p-3 bg-primary-green border border-link rounded-lg
        ${isDragging ? "opacity-50 z-50" : ""}
      `}
      {...attributes}
    >
      <div className="flex items-center space-x-2">
        <div className="w-6 h-6 bg-white text-link rounded-full flex items-center justify-center text-xs font-medium">
          {index + 1}
        </div>
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-white hover:text-white/70"
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center space-x-2 flex-1">
        <section.icon className="text-white h-4 w-4" />
        <BodyText size="sm" textColor="white">
          {section.label}
        </BodyText>
        {section.key === "timeline" && (
          <Badge className="text-xs" variant="light">
            (Timeline will always be last)
          </Badge>
        )}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="text-error"
        onClick={() => onRemove(section.key)}
      >
        Remove
      </Button>
    </div>
  );
};

export const MedicalRecordsPreferences = () => {
  const { data: user } = useUser();
  const { selectedSections, updateSelectedSections } =
    useMedicalRecordsPreferences();

  const form = useForm<MedicalRecordsPreferencesFormValues>({
    resolver: zodResolver(medicalRecordsPreferencesSchema),
    defaultValues: {
      selectedSections: selectedSections,
    },
  });

  // Sync form with hook state when it changes
  useEffect(() => {
    form.reset({ selectedSections });
  }, [selectedSections, form]);

  const watchedSelectedSections = form.watch("selectedSections");

  // Get selected sections with their configurations in order
  const selectedSectionConfigs = watchedSelectedSections
    .map((sectionKey) =>
      MEDICAL_RECORD_SECTIONS.find((section) => section.key === sectionKey),
    )
    .filter(
      (section): section is MedicalRecordSection => section !== undefined,
    );

  // Get unselected sections for the grid
  const unselectedSections = MEDICAL_RECORD_SECTIONS.filter(
    (section) => !watchedSelectedSections.includes(section.key),
  );

  const handleSectionToggle = (sectionKey: string, checked: boolean) => {
    if (checked && watchedSelectedSections.length >= 5) {
      form.setError("selectedSections", {
        type: "manual",
        message: "You can only select up to 5 sections for your Quick View.",
      });
      return;
    }

    // Clear any existing errors when toggling
    form.clearErrors("selectedSections");

    const newSelectedSections = checked
      ? [...watchedSelectedSections, sectionKey]
      : watchedSelectedSections.filter((section) => section !== sectionKey);

    form.setValue("selectedSections", newSelectedSections, {
      shouldDirty: true,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = watchedSelectedSections.findIndex(
        (id) => id === active.id,
      );
      const newIndex = watchedSelectedSections.findIndex(
        (id) => id === over.id,
      );

      const newSelectedSections = arrayMove(
        watchedSelectedSections,
        oldIndex,
        newIndex,
      );

      form.setValue("selectedSections", newSelectedSections, {
        shouldDirty: true,
      });
    }
  };

  const handleRemoveSection = (sectionKey: string) => {
    const newSelectedSections = watchedSelectedSections.filter(
      (section) => section !== sectionKey,
    );
    form.setValue("selectedSections", newSelectedSections, {
      shouldDirty: true,
    });
    form.clearErrors("selectedSections");
  };

  const handleSave = (data: MedicalRecordsPreferencesFormValues) => {
    updateSelectedSections(data.selectedSections);
    logMessage("Medical records preferences saved.", LogLevel.Info, {
      userId: user?.userId,
      selectedSections: data.selectedSections.join(", "),
    });
    toast.success("Preferences saved", {
      description:
        "Your Quick View has been updated with your selected sections.",
    });
  };

  const { isDirty } = useFormState({ control: form.control });
  const hasChanges = isDirty;

  return (
    <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
      <div>
        <HeaderText size="lg" className="mb-2">
          Quick View Sections
        </HeaderText>
        <BodyText textColor="secondary" className="mb-4">
          Select up to 5 medical record sections to display in your "Quick
          View". Drag and drop to reorder your selected sections.
        </BodyText>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {hasChanges && (
            <Button type="submit" size="sm">
              Save Changes
            </Button>
          )}
        </div>

        <Controller
          control={form.control}
          name="selectedSections"
          render={({ fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Selected Sections - Sortable */}
                <div className="space-y-3">
                  <HeaderText size="sm" className="text-gray-700">
                    Selected Sections ({watchedSelectedSections.length}/5)
                  </HeaderText>
                  {selectedSectionConfigs.length > 0 ? (
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={watchedSelectedSections}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2 min-h-[200px] p-3 border-2 border-dashed border-gray-200 rounded-lg">
                          <BodyText
                            size="xs"
                            textColor="tertiary"
                            className="mb-2"
                          >
                            Click and drag the sections to reorder them. To
                            drag, click and hold the rectangle between the
                            number and the section name/icon.
                          </BodyText>
                          {selectedSectionConfigs.map((section, index) => (
                            <SortableSelectedSection
                              key={section.key}
                              section={section}
                              index={index}
                              onRemove={handleRemoveSection}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="min-h-[200px] p-6 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                      <BodyText textColor="tertiary" className="text-center">
                        Select sections from the right to add them here
                      </BodyText>
                    </div>
                  )}
                </div>

                {/* Available Sections - Grid */}
                <div className="space-y-3">
                  <HeaderText size="sm" className="text-gray-700">
                    Available Sections ({unselectedSections.length} remaining)
                  </HeaderText>
                  <div className="min-h-[200px] space-y-2">
                    {unselectedSections.map((section: MedicalRecordSection) => {
                      const isDisabled = watchedSelectedSections.length >= 5;

                      return (
                        <button
                          key={section.key}
                          type="button"
                          className={`
                              flex items-center space-x-3 p-3 border rounded-lg transition-colors bg-white border-gray-200 text-left w-full
                              ${isDisabled ? "opacity-50" : "hover:bg-gray-50 cursor-pointer"}
                            `}
                          onClick={() =>
                            !isDisabled &&
                            handleSectionToggle(section.key, true)
                          }
                          disabled={isDisabled}
                          aria-label={`Add ${section.label} to selected sections`}
                        >
                          <Checkbox
                            id={section.key}
                            checked={false}
                            onCheckedChange={(checked) =>
                              handleSectionToggle(section.key, checked === true)
                            }
                            disabled={isDisabled}
                          />
                          <div className="flex items-center space-x-2 flex-1">
                            <section.icon className="text-gray-600 h-4 w-4" />
                            <label
                              htmlFor={section.key}
                              className={`text-sm cursor-pointer ${isDisabled ? "cursor-not-allowed" : ""}`}
                            >
                              {section.label}
                            </label>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>
    </form>
  );
};
